module Meevo.Plugins.TransactionEditor {
    export class InsertTransactionModalController {
        drawerSessionList: Model.DrawerSessionListItem[] = [];
        drawerSessionFiltered: Model.DrawerSessionListItem[] = [];
        dates: Date[] = [];

        private editTransactionThreshold: number = 0;
        private maxEditTransactionThreshold: number = 32;

        insertTransactionDate: Date;
        insertTransactionTime: Date;
        insertTransactionDrawerId: string;
        insertTransactionDrawerSessionId: string;

        todayDate: moment.Moment;
        minDate: moment.Moment;
        maxDate: moment.Moment;

        validationMessages;

        static $inject = [
            'startDateTime',
            'isCheckout',
            'callback',
            '$translate',
            'meevo.components.common.DataService',
            'meevo.components.modal.ModalService'
        ];

        constructor(
            startDateTime: Date,
            public isCheckout: boolean,
            private callback: Function,
            private $translate: angular.translate.ITranslateService,
            private dataService: Meevo.Components.BaseClasses.IDataService,
            private modalService: Meevo.Components.Modal.IModalService
        ) {
            this.loadEditTransactionThreshold().finally(() => {
                this.initialize(startDateTime);
            });
        }

        private loadEditTransactionThreshold() {
            return this.dataService.customMethodData("/api/securityRole/daysToEditTransaction", "GET", null)
                .then((response) => {
                    this.editTransactionThreshold = response.data || this.maxEditTransactionThreshold;
                })
                .catch(() => {
                    this.editTransactionThreshold = this.maxEditTransactionThreshold;
                });
        }

        private initialize(startDateTime) {
            this.todayDate = moment().startOf('day');

            const startDate = moment(startDateTime).startOf('day');
            const yesterdayDate = moment(this.todayDate).subtract(1, 'day');

            this.maxDate = moment(this.todayDate);
            this.minDate = this.isCheckout 
                ? moment(startDate).subtract(1, 'day')
                : moment(yesterdayDate).subtract(this.editTransactionThreshold, 'days');

            // if the given start date is not in the past, then use yesterday
            const insertTransactionDate = startDate.isInPast()
                ? startDate.toDate()
                : yesterdayDate.toDate();

            this.getDrawerSessionList(insertTransactionDate).then(() => {
                this.dates = this.buildDatesDropdown(this.minDate, this.maxDate);

                this.insertTransactionDate = this.dates.find(date => moment(date).isSame(insertTransactionDate));

                const startTime = moment(startDateTime).time();
                this.insertTransactionTime = startTime.toDate();

                this.updateInsertTransactionTime(this.insertTransactionDate);
            });
        }

        private buildDatesDropdown(minDate: moment.Moment, maxDate: moment.Moment): Date[] {
            let date = moment(minDate);
            let dates: Date[] = [];

            while (date.isBefore(maxDate)) {
                const jsDate = moment(date).toDate();
                if (this.insertTransactionDateFilter(jsDate)) {
                    dates.push(jsDate);
                }
                date.add('day', 1);
            }

            return dates;
        }

        private getDrawerSessionList(insertTransactionDate: Date) {
            const startDate = this.minDate.toDate();
            const endDate = moment(this.maxDate).subtract('day', 1).toDate();

            const request = {
                criteria: {
                    startDate: startDate,
                    endDate: endDate,
                    includeActive: true,
                    includeInactive: true
                },
                pageNumer: 0,
                itemsPerPage: 100,
            };

            return this.dataService.customMethodData(`/api/drawerSession/list`, "post", request)
                .then(response => {
                    this.drawerSessionList = response.data;
                    this.refreshDrawerSessionList(insertTransactionDate);
                })
                .catch(() => {
                    this.drawerSessionList = [];
                    this.drawerSessionFiltered = [];
                    this.insertTransactionDrawerId = undefined;
                    this.insertTransactionDrawerSessionId = undefined;
                });
        }

        private isInsertDateValid(dateTime: moment.Moment, drawer: Model.DrawerSessionListItem) {
            const openedDate = moment(drawer.openedDate).startOf('day');
            const closedDate = drawer.closedDate ? moment(drawer.closedDate).startOf('day') : null;
            const isSameOrAfterOpenedDate = dateTime.isSame(openedDate) || dateTime.isAfter(openedDate);
            const isSameOrBeforeClosedDate = !closedDate || dateTime.isSame(closedDate) || dateTime.isBefore(closedDate);
            return isSameOrAfterOpenedDate && isSameOrBeforeClosedDate;
        }

        private refreshDrawerSessionList(insertTransactionDate: Date) {
            const dateTime = moment(insertTransactionDate);

            let drawerSessionFiltered = this.drawerSessionList.filter(drawer => this.isInsertDateValid(dateTime, drawer));

            if (this.insertTransactionDrawerSessionId && _(drawerSessionFiltered).all(item => item.id != this.insertTransactionDrawerSessionId)) {
                this.insertTransactionDrawerSessionId = undefined;
                this.insertTransactionDrawerId = undefined;
            }

            if (!this.insertTransactionDrawerSessionId && drawerSessionFiltered.length > 0) {
                const firstDrawerSession = drawerSessionFiltered[0];
                this.insertTransactionDrawerId = firstDrawerSession.drawerId;
                this.insertTransactionDrawerSessionId = firstDrawerSession.id;
            }

            this.drawerSessionFiltered = drawerSessionFiltered;
        }

        private updateInsertTransactionTime(insertTransactionDate: Date) {
            this.insertTransactionTime = new Date(
                insertTransactionDate.getFullYear(),
                insertTransactionDate.getMonth(),
                insertTransactionDate.getDate(),
                this.insertTransactionTime.getHours(),
                this.insertTransactionTime.getMinutes()
            );
        }

        drawerSessionChanged() {
            this.updateInsertTransactionTime(this.insertTransactionDate);
            
            const drawerSession = this.getSelectedDrawerSession();
            this.insertTransactionDrawerId = drawerSession.drawerId;
        }

        insertTransactionDateChanged() {
            this.updateInsertTransactionTime(this.insertTransactionDate);
            this.refreshDrawerSessionList(this.insertTransactionDate);
        }

        insertTransactionDateFilter = (date: Date): boolean => {
            const isInRange = moment(date).isBetween(this.minDate, this.maxDate);

            if (!isInRange) {
                return false;
            }

            return _(this.drawerSessionList).any(drawer => this.isInsertDateValid(moment(date), drawer));
        }

        private isValidTime(insertTransactionTime: Date) {
            const drawerSessionOpenedTime = moment(this.getRangeStartTime());
            const drawerSessionClosedTime = moment(this.getRangeEndTime());

            const time = moment(insertTransactionTime);
            const isValidTime = (time >= drawerSessionOpenedTime) && (time <= drawerSessionClosedTime);

            return isValidTime;
        }

        isValid() {
            if (!this.drawerSessionList || this.drawerSessionList.length === 0) {
                // not setup yet
                return true;
            }

            if (!this.drawerSessionFiltered || this.drawerSessionFiltered.length === 0 ||
                !this.insertTransactionDrawerSessionId) {
                return false;
            }

            const drawerSession = this.getSelectedDrawerSession();
            if (!drawerSession) {
                return false;
            }

            const isValidDate = this.insertTransactionDateFilter(this.insertTransactionDate);
            const isValidTime = this.isValidTime(this.insertTransactionTime);
            
            return isValidDate && isValidTime;
        }

        continue() {
            if (!this.isValid()) {
                return;
            }

            this.insertTransaction();
            this.close();
        }

        close() {
            this.modalService.hide();
        }

        cancel() {
            this.modalService.hide();
        }

        insertTransaction() {
            //this.dataService.trackUsage(Meevo.Model.NounEnum.TransactionEditorPage, Meevo.Model.VerbEnum.InsertTransactionButtonClicked, null, item.transactionGuid);

            if (this.callback) {
                const pastDateTime = moment(this.insertTransactionDate).add(moment.duration(this.insertTransactionTime)).toDate();

                const pastTransactionContext: Meevo.Model.PastTransactionContext = {
                    pastDateTime: pastDateTime,
                    drawerId: this.insertTransactionDrawerId,
                    drawerSessionId: this.insertTransactionDrawerSessionId,
                };

                this.callback(pastTransactionContext);
            }
        }

        private getSelectedDrawerSession(): Model.DrawerSessionListItem {
            return _(this.drawerSessionFiltered).find(drawerSession => drawerSession.id == this.insertTransactionDrawerSessionId);
        }

        getValidationMessages() {
            if (!this.validationMessages) {
                this.validationMessages = {
                    soonerMessage: this.$translate.instant('MESSAGE_INSERT_TRANSACTION_TIME_VALIDATION'),
                    laterMessage:  this.$translate.instant('MESSAGE_INSERT_TRANSACTION_TIME_VALIDATION'),
                };
            }

            return this.validationMessages;
        }

        getRangeStartTime(): Date {
            const drawerSession = this.getSelectedDrawerSession();

            if (!drawerSession || !this.insertTransactionDate) {
                return;
            }

            if (moment(drawerSession.openedDate) < moment(this.insertTransactionDate)) {
                return this.insertTransactionDate;
            }

            return drawerSession.openedDate;
        }

        getRangeEndTime(): Date {
            const drawer = this.getSelectedDrawerSession();

            if (!drawer || !this.insertTransactionDate) {
                return;
            }

            return drawer.closedDate || this.todayDate.toDate();
        }
    }

    angular.module("meevo.plugins.transactionEditor")
        .controller("meevo.plugins.transactionEditor.InsertTransactionModalController", InsertTransactionModalController);
}